import {
  useState,
  useRef,
  useCallback,
  forwardRef,
  type MouseEvent,
  type ComponentProps,
} from 'react';
import { useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { CopyIcon, CheckIcon } from '@phosphor-icons/react';

export const COPY_BUTTON_FEEDBACK_MS = 1500;

type ButtonSize = NonNullable<ComponentProps<typeof Button>['size']>;

interface CopyButtonProps {
  value: string;
  title?: string;
  className?: string;
  iconClassName?: string;
  size?: ButtonSize;
  onClick?: (e: MouseEvent) => void;
  /**
   * When set, the checkmark state is controlled by the parent (e.g. a single
   * highlighted row in a table). Clipboard write still happens on click;
   * call `onCopySuccess` from CopyButton to drive this prop.
   */
  copied?: boolean;
  /** Fires after text is written; use with `copied` for coordinated table UI. */
  onCopySuccess?: (value: string) => void;
}

export const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  function CopyButton(
    {
      value,
      title,
      className = 'h-6 w-6 p-0',
      iconClassName = 'h-3.5 w-3.5',
      size = 'sm',
      onClick,
      copied: copiedProp,
      onCopySuccess,
    },
    ref,
  ) {
    const gt = useGT();
    const resolvedTitle =
      title ?? gt('Copy', { context: 'copy button tooltip' });
    const [internalCopied, setInternalCopied] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
      undefined,
    );

    const isControlled = copiedProp !== undefined;
    const showCopied = isControlled ? copiedProp : internalCopied;

    const handleClick = useCallback(
      async (e: MouseEvent) => {
        e.stopPropagation();
        onClick?.(e);
        try {
          await navigator.clipboard?.writeText(value);
        } catch {
          return;
        }
        if (isControlled) {
          onCopySuccess?.(value);
        } else {
          setInternalCopied(true);
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(
            () => setInternalCopied(false),
            COPY_BUTTON_FEEDBACK_MS,
          );
        }
      },
      [value, onClick, isControlled, onCopySuccess],
    );

    return (
      <Button
        ref={ref}
        variant="ghost"
        size={size}
        className={className}
        onClick={handleClick}
        title={
          showCopied
            ? gt('Copied!', { context: 'copy button after successful copy' })
            : resolvedTitle
        }
        aria-label={
          showCopied
            ? gt('Copied!', { context: 'copy button after successful copy' })
            : resolvedTitle
        }
      >
        {showCopied ? (
          <CheckIcon className={`${iconClassName} text-green-600`} />
        ) : (
          <CopyIcon className={iconClassName} />
        )}
      </Button>
    );
  },
);
